import React, {useState, useEffect, useMemo} from 'react';
import {
  View,
  Dimensions,
  Pressable,
  Platform,
  useWindowDimensions,
} from 'react-native';
import {LineChart} from 'react-native-chart-kit';
import {FONTS, KEYS} from '../../../utils/Constants';
import {DLog, formatTimeByPreference} from '../../../utils/HelperMethods';
import {getData} from '../../../utils/GeneralStorage';
import Svg, {Text as TextSVG, Line} from 'react-native-svg';

import Text from '../../elements/Text';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {DownArrowIcon, RightArrowIcon} from '../../../utils/SvgIcons';

const BloodSugarGraph = ({
  homeMode,
  min,
  max,
  allMeasurements = false,
  readings,
  numDays,
  startDate,
  filterDate,
  WebArrows,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  //mode can be home or blood sugar history
  const padding = 16; //the linechart requires a fixed width prop so setting/using padding from here for consistent UI
  const graphMin = 0;
  const graphMax = 400;
  const graphHeight = 150;
  const numLimit = 10; //we wont show more than these many readings. Configure according to screen size

  // Called unconditionally (rules-of-hooks); native keeps using Dimensions.get
  // below so its width behavior is unchanged.
  const windowDimensions = useWindowDimensions();
  const width = //important parameter for making layouts responsive. Always define as a ratio
    Platform.OS === 'web'
      ? allMeasurements
        ? windowDimensions.width / 1.75
        : windowDimensions.width / (homeMode ? 2.5 : 3.5)
      : Dimensions.get('window').width;

  const [clickedDot, setClickedDot] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  const [timeFormat, setTimeFormat] = useState('24');

  const maxLabelsWithoutRotation = useMemo(() => {
    if (Platform.OS === 'web' && homeMode) {
      return 8;
    } else {
      return 6;
    }
  }, [homeMode]);

  useEffect(() => {
    setClickedDot({});
  }, [filterDate]);

  // Load time format preference
  useEffect(() => {
    const loadTimeFormat = async () => {
      const format = await getData(KEYS.timeFormat);
      if (format) {
        setTimeFormat(format);
      }
    };
    loadTimeFormat();
  }, []);

  let xLabels = [];
  let xLabelsWithDelta = [];
  let delta = 0;
  let yData = [];
  let verticalLabelRotation = 0;
  let xLabelsOffset = 0;

  const getYLabelsForAllMeasurements = () => {
    //calculate daily average for each day in O(n)
    let dict = {};
    for (let i = numDays - 1; i >= 0; i--) {
      let date = new Date(startDate);
      date.setDate(date.getDate() - i);
      let day = date.getDate();
      let month = date.getMonth();
      let year = date.getFullYear();
      let dayKey = `${year}-${month}-${day}`;
      dict[dayKey] = [];
    }
    for (let i = 0; i < readings.data.length; i++) {
      let entry = readings.data[i];
      let date = new Date(entry.measureAt);
      // DLog('BloodSugarGraph', date.toISOString());
      let day = date.getDate();
      let month = date.getMonth();
      let year = date.getFullYear();
      let dayKey = `${year}-${month}-${day}`;
      // DLog('BloodSugarGraph', dayKey);
      if (!dict[dayKey]) {
        dict[dayKey] = [];
      }
      dict[dayKey].push(entry.diabetesMeasurement.bloodSugarCount);
    }
    for (let key in dict) {
      let sum = 0;
      for (let i = 0; i < dict[key].length; i++) {
        //Still O(n) since iterable length = n = readings.data.length
        sum += parseInt(dict[key][i]);
      }
      yData.push(sum > 0 ? sum / dict[key].length : 0);
    }
    // DLog('BloodSugarGraph', dict, yData);
  };

  //make a separate function for computing x labels
  const getXLabelsForDailyTrendGraph = () => {
    let filterDateStart = new Date(filterDate);
    filterDateStart.setHours(0, 0, 0, 0);
    filterDateStart = filterDateStart.toISOString();

    let filterDateEnd = new Date(filterDate);
    filterDateEnd.setHours(23, 59, 59, 999);
    filterDateEnd = filterDateEnd.toISOString();

    readings.data.forEach(entry => {
      if (
        filterDateStart <= entry.measureAt &&
        filterDateEnd >= entry.measureAt
      ) {
        const entryDate = new Date(entry.measureAt);
        let xLabel;

        if (timeFormat === '12') {
          // 12-hour format: "2:30 PM"
          let hours = entryDate.getHours();
          const minutes = entryDate.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12;
          hours = hours ? hours : 12; // 0 should be 12
          const minutesStr = minutes < 10 ? '0' + minutes : minutes;
          xLabel = `${hours}:${minutesStr} ${ampm}`;
        } else {
          // 24-hour format: "14:30"
          xLabel = entryDate.toString().substring(16, 21);
        }

        xLabels.push(xLabel);
        //get y data
        let value = entry.diabetesMeasurement.bloodSugarCount;
        if (value > graphMax) yData.push(graphMax);
        else if (value < graphMin) yData.push(graphMin);
        else yData.push(value);
      }
    });
    if (Platform.OS !== 'web') {
      verticalLabelRotation =
        xLabels.length > maxLabelsWithoutRotation ? -45 : 0;
    }
    xLabelsOffset = xLabels.length > maxLabelsWithoutRotation ? 10 : 0;
    delta = Math.ceil(xLabels.length / numLimit);
    xLabelsWithDelta = xLabels.map((val, index) => {
      if (index % delta === 0) return val;
      else return '';
    });
  };

  const getXLabelsForAllMeasurements = () => {
    let array5 = ['', '', '', '', ''];
    let array6 = ['', '', '', '', '', ''];
    let array7 = ['', '', '', '', '', '', ''];

    switch (
      numDays //get all measuerments x labels
    ) {
      case 7:
        xLabels = [1, '', 3, '', 5, '', 7];
        break;
      case 30:
        xLabels = [1, ...array6, 15, ...array6, 30];
        break;
      case 60:
        xLabels = [
          1,
          ...array7,
          15,
          ...array7,
          30,
          ...array7,
          45,
          ...array7,
          60,
        ];
        break;
      default: //if numDays < 7 list all days from 1-6 and otherwise split days into 5 equal parts
        if (numDays < 7) {
          for (let i = 1; i <= numDays; i++) {
            xLabels.push(i);
          }
        } else {
          xLabels.push(1, ...array5);
          let midElement = Math.floor(numDays / 2);
          xLabels.push(Math.floor(midElement / 2), ...array5);
          xLabels.push(midElement, ...array5);
          xLabels.push(midElement + Math.floor(midElement / 2), ...array5);
          xLabels.push(numDays);
        }
    }
  };

  if (readings?.data?.length > 0) {
    if (!allMeasurements) {
      //get x labels for just the filterDate
      getXLabelsForDailyTrendGraph();
      // DLog('BloodSugarGraph recomputing labels,verticalLabelRotation', xLabels, yData,xLabels.length>10,verticalLabelRotation);
    } else {
      //get x labels for all measurements
      getXLabelsForAllMeasurements();
      getYLabelsForAllMeasurements();
    }
  } else xLabels = ['', 'No Readings', ''];

  const giveLine = height => {
    //now we defined a function which gives the line with y on height
    const lineHeight = graphHeight - (graphHeight / 400) * height + padding;
    return (
      <Svg>
        <TextSVG
          x={40}
          y={lineHeight + 5} //offset to make it in the middle
          fill={colors.textMuted}
          fontFamily={FONTS.regularFont}
          fontSize="14"
          textAnchor="end">
          {height}
        </TextSVG>
        <Line
          x1={50}
          y1={lineHeight}
          x2={width - 40}
          y2={lineHeight}
          stroke={colors.borderMuted}
          strokeWidth={1}
          strokeDasharray={2}
        />
      </Svg>
    );
  };

  const giveXLabelLine = () => {
    const xRotation =
      xLabelsWithDelta.length > maxLabelsWithoutRotation ? '-45deg' : '0deg';
    const xLabelPadding =
      (xLabelsWithDelta.length > maxLabelsWithoutRotation ? 6 : 4) * padding;
    return (
      <Svg>
        {xLabelsWithDelta.map((label, i) => (
          <Svg
            x={i * ((width - xLabelPadding) / xLabelsWithDelta.length) + 48}
            y={xLabelsWithDelta.length > maxLabelsWithoutRotation ? 200 : 190} //offset to make it in the middle]
            style={{overflow: 'visible'}}>
            <TextSVG
              key={i}
              fill={colors.textMuted}
              fontFamily={FONTS.regularFont}
              fontSize="14"
              textAnchor="start"
              style={{transform: [{rotate: xRotation}]}}>
              {label}
            </TextSVG>
          </Svg>
        ))}
      </Svg>
    );
  };

  const decorator = () => {
    return (
      <Svg>
        {giveLine(400)}
        {giveLine(max)}
        {giveLine(min)}
        {giveLine(0)}
        <ToolTip />
        {Platform.OS === 'web' && !allMeasurements && giveXLabelLine()}
      </Svg>
    );
  };

  const ToolTip = () => {
    // DLog(data);
    // DLog(clickedDot, xLabels[clickedDot.index]);
    return clickedDot.x ? (
      <Svg>
        <TextSVG
          x={clickedDot.x}
          y={clickedDot.y > 32 ? clickedDot.y - 12 : clickedDot.y + 24}
          fill={colors.link}
          fontFamily={FONTS.boldFont}
          fontSize="14"
          fontWeight="bold"
          textAnchor="middle">
          {clickedDot.value}
        </TextSVG>
        <TextSVG
          x={clickedDot.x}
          y={clickedDot.y > 32 ? clickedDot.y + 24 : clickedDot.y + 36}
          fill={colors.link}
          fontFamily={FONTS.boldFont}
          fontSize="14"
          fontWeight="bold"
          textAnchor="middle">
          {xLabels[clickedDot.index]}
        </TextSVG>
        <Line
          x1={clickedDot.x}
          y1={clickedDot.y > 32 ? clickedDot.y + 6 : clickedDot.y + 26}
          x2={clickedDot.x}
          y2={168}
          stroke={colors.link}
          strokeWidth={2}
          strokeDasharray={3}
        />
      </Svg>
    ) : null;
  };

  const Title = () => {
    return (
      <View style={styles.titleStyle}>
        {homeMode ? (
          <Text color={colors.textHeading} variation="Subtitle1Regular">
            My Daily Trend
          </Text>
        ) : (
          <Text color={colors.textHeading} variation="Subtitle1Regular">
            Period Trend:{' '}
            <Text color={colors.accentStrong} variation="Subtitle1Bold">
              {filterDate
                ? new Date(filterDate).toDateString().substring(4, 10)
                : filterDate}
            </Text>
          </Text>
        )}
        {homeMode ? null : WebArrows}
        {Platform.OS !== 'web' ? (
          <Pressable onPress={() => setIsVisible(!isVisible)}>
            <View style={styles.centerItem} hitSlop={styles.hitSlop}>
              {isVisible ? (
                <RightArrowIcon tintColor={colors.link} />
              ) : (
                <DownArrowIcon tintColor={colors.link} />
              )}
            </View>
          </Pressable>
        ) : null}
      </View>
    );
  };

  return allMeasurements ? (
    <View style={styles.globalPadding(padding)}>
      <View style={styles.mainCardStyle}>
        <View style={styles.elementPadding(padding)} />
        <LineChart
          data={{
            labels: xLabels,
            datasets: [
              {
                data: [graphMin],
                withDots: false,
              },
              {
                data: [graphMax],
                withDots: false,
              },
              {
                data: yData.length > 0 ? yData : [0],
                color: (opacity = 1) => colors.chartSeries,
                strokeWidth: 3,
                withDots: false,
              },
            ],
          }}
          width={numDays > 7 ? width - 2 * padding : width} // from react-native
          height={200}
          bezier
          withVerticalLines={false}
          withHorizontalLabels={false}
          withHorizontalLines={false}
          decorator={decorator}
          fromZero={true}
          chartConfig={styles.chartConfig}
        />
      </View>
    </View>
  ) : (
    <View style={styles.globalPadding(padding)}>
      <View style={styles.mainCardStyle}>
        <View style={styles.elementPadding(padding)}>
          <Title />
        </View>
        {isVisible && (
          <LineChart
            data={{
              labels: Platform.OS === 'web' ? [] : xLabelsWithDelta,
              datasets: [
                {
                  data: [graphMin],
                  withDots: false,
                },
                {
                  data: [graphMax],
                  withDots: false,
                },
                {
                  data: yData.length > 0 ? yData : [0],
                  color: (opacity = 1) => colors.chartSeries,
                  strokeWidth: 3,
                  withDots: yData.length > 0 ? true : false,
                },
              ],
            }}
            width={xLabelsOffset == 10 ? width - 2 * padding : width} // from react-native
            height={200}
            withVerticalLines={false}
            withHorizontalLabels={false}
            withHorizontalLines={false}
            onDataPointClick={setClickedDot}
            decorator={decorator}
            fromZero={true}
            chartConfig={styles.chartConfig}
            verticalLabelRotation={verticalLabelRotation}
            xLabelsOffset={xLabelsOffset}
          />
        )}
      </View>
    </View>
  );
};

export default BloodSugarGraph;

//Old version of the graph, keeping for agility reasons
// const BloodSugarGraph2 = ({
//   homeMode,
//   min,
//   max,
//   allMeasurements = false,
//   readings,
//   numDays,
//   startDate,
//   filterDate,
// }) => {
//   //mode can be home or blood sugar history
//   const padding = 16; //the linechart requires a fixed width prop so setting/using padding from here for consistent UI
//   const graphMin = 2 * min - max;
//   const graphMax = 2 * max - min;

//   const [clickedDot, setClickedDot] = useState({});
//   const [isVisible, setIsVisible] = useState(true);

//   useEffect(() => {
//     setClickedDot({});
//   }, [filterDate]);

//   let xLabels = [];
//   let yData = [];
//   let verticalLabelRotation = 0;
//   let xLabelsOffset = 0;

//   const getYLabelsForAllMeasurements = () => {
//     //calculate daily average for each day in O(n)
//     let dict = {};
//     for (let i = numDays - 1; i >= 0; i--) {
//       let date = new Date(startDate);
//       date.setDate(date.getDate() - i);
//       let day = date.getDate();
//       let month = date.getMonth();
//       let year = date.getFullYear();
//       let dayKey = `${year}-${month}-${day}`;
//       dict[dayKey] = [];
//     }
//     for (let i = 0; i < readings.data.length; i++) {
//       let entry = readings.data[i];
//       let date = new Date(entry.measureAt);
//       // DLog('BloodSugarGraph', date.toISOString());
//       let day = date.getDate();
//       let month = date.getMonth();
//       let year = date.getFullYear();
//       let dayKey = `${year}-${month}-${day}`;
//       // DLog('BloodSugarGraph', dayKey);
//       if (!dict[dayKey]) {
//         dict[dayKey] = [];
//       }
//       dict[dayKey].push(entry.diabetesMeasurement.bloodSugarCount);
//     }
//     for (let key in dict) {
//       let sum = 0;
//       for (let i = 0; i < dict[key].length; i++) {
//         //Still O(n) since iterable length = n = readings.data.length
//         sum += parseInt(dict[key][i]);
//       }
//       yData.push(sum > 0 ? sum / dict[key].length : 0);
//     }
//     // DLog('BloodSugarGraph', dict, yData);
//   };

//   //make a separate function for computing x labels
//   const getXLabelsForDailyTrendGraph = () => {
//     let filterDateStart = new Date(filterDate);
//     filterDateStart.setHours(0, 0, 0, 0);
//     filterDateStart = filterDateStart.toISOString();

//     let filterDateEnd = new Date(filterDate);
//     filterDateEnd.setHours(23, 59, 59, 999);
//     filterDateEnd = filterDateEnd.toISOString();

//     readings.data.forEach(entry => {
//       if (
//         filterDateStart <= entry.measureAt &&
//         filterDateEnd >= entry.measureAt
//       ) {
//         let xLabel = new Date(entry.measureAt).toString().substring(16, 21);
//         xLabels.push(xLabel); //TODO:make these localdatetime
//         //get y data
//         let value = entry.diabetesMeasurement.bloodSugarCount;
//         if (value > graphMax) yData.push(graphMax);
//         else if (value < graphMin) yData.push(graphMin);
//         else yData.push(value);
//       }
//     });
//     verticalLabelRotation = xLabels.length > 8 ? -45 : 0;
//     xLabelsOffset = xLabels.length > 8 ? 10 : 0;
//   };

//   const getXLabelsForAllMeasurements = () => {
//     let array5 = ['', '', '', '', ''];
//     let array6 = ['', '', '', '', '', ''];
//     let array7 = ['', '', '', '', '', '', ''];

//     switch (
//       numDays //get all measuerments x labels
//     ) {
//       case 7:
//         xLabels = [1, '', 3, '', 5, '', 7];
//         break;
//       case 30:
//         xLabels = [1, ...array6, 15, ...array6, 30];
//         break;
//       case 60:
//         xLabels = [
//           1,
//           ...array7,
//           15,
//           ...array7,
//           30,
//           ...array7,
//           45,
//           ...array7,
//           60,
//         ];
//         break;
//       default: //if numDays < 7 list all days from 1-6 and otherwise split days into 5 equal parts
//         if (numDays < 7) {
//           for (let i = 1; i <= numDays; i++) {
//             xLabels.push(i);
//           }
//         } else {
//           xLabels.push(1, ...array5);
//           let midElement = Math.floor(numDays / 2);
//           xLabels.push(Math.floor(midElement / 2), ...array5);
//           xLabels.push(midElement, ...array5);
//           xLabels.push(midElement + Math.floor(midElement / 2), ...array5);
//           xLabels.push(numDays);
//         }
//     }
//   };

//   if (readings?.data?.length > 0) {
//     if (!allMeasurements) {
//       //get x labels for just the filterDate
//       getXLabelsForDailyTrendGraph();
//       // DLog('BloodSugarGraph recomputing labels,verticalLabelRotation', xLabels, yData,xLabels.length>10,verticalLabelRotation);
//     } else {
//       //get x labels for all measurements
//       getXLabelsForAllMeasurements();
//       getYLabelsForAllMeasurements();
//     }
//   } else xLabels = ['', 'No Readings', ''];

//   function* yLabel() {
//     yield* ['', 'Hypo ', 'Med  ', 'Hyper'];
//   }
//   const yLabelIterator = yLabel();

//   const toolTip = data => {
//     // DLog(data);
//     // DLog(clickedDot);
//     return clickedDot.x ? (
//       <View>
//         <Svg>
//           <TextSVG
//             x={clickedDot.x}
//             y={clickedDot.y > 32 ? clickedDot.y - 12 : clickedDot.y + 24}
//             fill={COLORS.mainLargeText}
//             fontFamily={FONTS.boldFont}
//             fontSize="14"
//             fontWeight="bold"
//             textAnchor="middle">
//             {clickedDot.value}
//           </TextSVG>
//           <Line
//             x1={clickedDot.x}
//             y1={clickedDot.y > 32 ? clickedDot.y + 6 : clickedDot.y + 26}
//             x2={clickedDot.x}
//             y2={168}
//             stroke={COLORS.mainLargeText}
//             strokeWidth={2}
//             strokeDasharray={3}
//           />
//         </Svg>
//       </View>
//     ) : null;
//   };

//   const Title = () => {
//     return (
//       <View style={styles.titleStyle}>
//         {homeMode ? (
//           <Text color={COLORS.blueDarkText} variation="Subtitle1Regular">
//             My Daily Trend
//           </Text>
//         ) : (
//           <Text color={COLORS.blueDarkText} variation="Subtitle1Regular">
//             Period Trend:{' '}
//             <Text color={COLORS.blueMain1} variation="Subtitle1Bold">
//               {filterDate
//                 ? new Date(filterDate).toDateString().substring(4, 10)
//                 : filterDate}
//             </Text>
//           </Text>
//         )}
//         <Pressable onPress={() => setIsVisible(!isVisible)}>
//           <View style={styles.centerItem} hitSlop={styles.hitSlop}>
//             {isVisible ? (
//               <RightArrowIcon tintColor={COLORS.mainLargeText} />
//             ) : (
//               <DownArrowIcon tintColor={COLORS.mainLargeText} />
//             )}
//           </View>
//         </Pressable>
//       </View>
//     );
//   };

//   return allMeasurements ? (
//     <View style={styles.globalPadding(padding)}>
//       <View style={styles.mainCardStyle}>
//         <LineChart
//           data={{
//             labels: xLabels,
//             datasets: [
//               {
//                 data: [graphMin],
//                 withDots: false,
//               },
//               {
//                 data: [graphMax],
//                 withDots: false,
//               },
//               {
//                 data: yData.length > 0 ? yData : [0],
//                 color: (opacity = 1) => COLORS.blueMain2,
//                 strokeWidth: 3,
//                 withDots: false,
//               },
//             ],
//           }}
//           width={
//             numDays > 7
//               ? Dimensions.get('window').width - 2 * padding
//               : Dimensions.get('window').width
//           } // from react-native
//           height={200}
//           bezier
//           withVerticalLines={false}
//           segments={3}
//           formatYLabel={() => yLabelIterator.next().value}
//           onDataPointClick={setClickedDot}
//           decorator={data => toolTip(data)}
//           chartConfig={styles.chartConfig}
//         />
//       </View>
//     </View>
//   ) : (
//     <View style={styles.globalPadding(padding)}>
//       <View style={styles.mainCardStyle}>
//         <View style={styles.elementPadding(padding)}>
//           <Title />
//         </View>
//         {isVisible && (
//           <LineChart
//             data={{
//               labels: xLabels,
//               datasets: [
//                 {
//                   data: [graphMin],
//                   withDots: false,
//                 },
//                 {
//                   data: [graphMax],
//                   withDots: false,
//                 },
//                 {
//                   data: yData.length > 0 ? yData : [0],
//                   color: (opacity = 1) => COLORS.blueMain2,
//                   strokeWidth: 3,
//                   withDots: yData.length > 0 ? true : false,
//                 },
//               ],
//             }}
//             width={
//               xLabelsOffset == 10
//                 ? Dimensions.get('window').width - 2 * padding
//                 : Dimensions.get('window').width
//             } // from react-native
//             height={200}
//             withVerticalLines={false}
//             segments={3}
//             formatYLabel={() => yLabelIterator.next().value}
//             onDataPointClick={setClickedDot}
//             decorator={data => toolTip(data)}
//             chartConfig={styles.chartConfig}
//             verticalLabelRotation={verticalLabelRotation}
//             xLabelsOffset={xLabelsOffset}
//           />
//         )}
//       </View>
//     </View>
//   );
// };
